import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Input,
    Checkbox,
    Select,
    Button,
    Modal,
    Icon,
} from "semantic-ui-react";
import { isSameDay, setYear, setMonth, setDate } from "date-fns";
import GolfCourse from "../../../models/GolfCourse";
import DatePicker from "../../../assets/components/DatePicker";

const pad2 = (n) => String(n).padStart(2, "0");
const hourLabel = (hour) => `${pad2(hour)}:00 - ${pad2(hour)}:59`;

const hourRangeLabel = (startHour, endHour) => {
    if (startHour === endHour) return hourLabel(startHour);
    return `${pad2(startHour)}:00 - ${pad2(endHour)}:59`;
};

const extractPriceSuffixFromTimeslotText = (text) => {
    // e.g. "06:00 - 06:59 (£150/pp)" -> "(£150/pp)"
    if (!text || typeof text !== "string") return "";
    const m = text.match(/\(([^)]+)\)\s*$/);
    return m ? `(${m[1]})` : "";
};

const groupTimeslotsForOverlay = (slots = [], { isVoucherBooking }) => {
    const sorted = [...slots]
        .filter((s) => Number.isFinite(s?.hour))
        .sort((a, b) => a.hour - b.hour);

    const groups = [];
    let current = null;

    const getKey = (slot) => {
        // For vouchers, pricing is hidden, so just group contiguous hours.
        if (isVoucherBooking) return "voucher";
        // Prefer numeric price stability
        if (typeof slot?.price === "number") return `price:${slot.price}`;
        // Fallback to whatever pricing suffix exists in text
        return `suffix:${extractPriceSuffixFromTimeslotText(slot?.timeslot)}`;
    };

    for (const slot of sorted) {
        const key = getKey(slot);

        if (
            !current ||
            slot.hour !== current.endHour + 1 ||
            key !== current.key
        ) {
            current = {
                key,
                startHour: slot.hour,
                endHour: slot.hour,
                priceSuffix: isVoucherBooking
                    ? ""
                    : extractPriceSuffixFromTimeslotText(slot.timeslot),
            };
            groups.push(current);
        } else {
            current.endHour = slot.hour;
        }
    }

    return groups.map((g) => ({
        key: `${g.startHour}-${g.endHour}-${g.key}`,
        text: `${hourRangeLabel(g.startHour, g.endHour)}${
            g.priceSuffix ? ` ${g.priceSuffix}` : ""
        }`,
    }));
};

const parseHourFromTimeslotString = (s) => {
    if (!s || typeof s !== "string") return null;
    const m = s.match(/^(\d{2}):(\d{2})/);
    if (!m) return null;
    const hour = parseInt(m[1], 10);
    return Number.isFinite(hour) ? hour : null;
};

const CourseAvailabilityCalendar = ({
    dateValue = new Date(),

    // Backwards compatible:
    // - existing callers pass a string like "10:00 - 11:00"
    // New preferred:
    // - callers can pass a number hour (0..23)
    timeValue = "10:00 - 11:00",

    member,
    course,
    voucher,
    onChangeDate,
    onChangeTime,

    // New (optional) callbacks for parent sanity:
    onLoadedAvailability = null,
    onOverrideChange = null,

    isCreditBooking = false,
    showTimeslotSelection = false,
    showTimeInput = false,
    bookingId = null,
    defaultFirstAvailable = false,
}) => {
    const [month, setSelectedMonth] = useState(dateValue.getMonth());
    const [year, setSelectedYear] = useState(dateValue.getFullYear());
    const [showAvailableTimesModal, setShowAvailableTimesModal] =
        useState(false);
    // New payload shape:
    // [{ day, poa, timeslots: [{ timeslot, hour, price }] }]
    const [availableDates, setAvailableDates] = useState([]);

    const [selectedDayAvailability, setSelectedDayAvailability] =
        useState(null);
    const [overrideAvailableDates, setOverrideAvailableDates] = useState(false);

    // Determine whether timeValue is hour or string
    const timeValueHour = useMemo(() => {
        if (typeof timeValue === "number") return timeValue;
        return parseHourFromTimeslotString(timeValue);
    }, [timeValue]);

    const requestSeqRef = useRef(0);
    const initialAutoselectDoneRef = useRef(false);

    const monthChangeTimerRef = useRef(null);

    const buildOverrideOptions = () =>
        Array.from({ length: 24 }, (_, hour) => ({
            key: hour,
            value: hour, // numeric hour
            text: hourLabel(hour), // "06:00 - 06:59"
        }));

    const buildDayOptions = (dayAvailability) => {
        const slots = dayAvailability?.timeslots ?? [];

        // If API returns fully formatted timeslot strings (including pricing),
        // use them as-is.
        // We'll still include hour/price so parents can use it if needed later.
        return slots.map((t) => ({
            key: t.hour,
            value: t.hour, // numeric hour
            text: t.timeslot,
            _raw: t,
        }));
    };

    const currentOptions = useMemo(() => {
        const PLEASE_SELECT_OPTION = {
            key: "__please_select__",
            value: null,
            text: "Please select a timeslot",
            disabled: true,
        };
        const opts =
            overrideAvailableDates === true
                ? buildOverrideOptions()
                : buildDayOptions(selectedDayAvailability);

        return [PLEASE_SELECT_OPTION, ...opts];
    }, [overrideAvailableDates, selectedDayAvailability]);

    const parseMinuteFromTimeString = (s) => {
        if (!s || typeof s !== "string") return null;
        const m = s.match(/^(\d{2}):(\d{2})$/);
        if (!m) return null;
        const hh = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
        if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
        return { hh, mm, minutes: hh * 60 + mm };
    };

    const clampToNearestAvailableHour = (minutes, availableHours) => {
        if (!Array.isArray(availableHours) || availableHours.length === 0)
            return null;

        let bestHour = availableHours[0];
        let bestDiff = Math.abs(bestHour * 60 - minutes);

        for (const h of availableHours) {
            const diff = Math.abs(h * 60 - minutes);
            if (diff < bestDiff) {
                bestDiff = diff;
                bestHour = h;
            }
        }

        return bestHour;
    };

    const isVoucherBooking = !!voucher;

    // In your payload, `poa` is on the day object (and you’re treating that as “month POA” behaviour)
    const isPoaForPeriod = useMemo(() => {
        // Month-level POA: it’s repeated on each day row in the response
        if (!Array.isArray(availableDates) || availableDates.length === 0)
            return false;
        return availableDates[0]?.poa === true;
    }, [availableDates]);

    const availableSlotHours = useMemo(() => {
        const slots = selectedDayAvailability?.timeslots ?? [];
        return slots
            .map((t) => t.hour)
            .filter((h) => Number.isFinite(h))
            .sort((a, b) => a - b);
    }, [selectedDayAvailability]);

    const availableSlotsForHelper = useMemo(() => {
        const slots = selectedDayAvailability?.timeslots ?? [];
        if (isVoucherBooking) {
            return slots.map((t) => ({
                key: t.hour,
                text: hourLabel(t.hour), // no pricing for vouchers
            }));
        }
        return slots.map((t) => ({
            key: t.hour,
            text: t.timeslot, // API formatted, includes pricing for cash/credit
        }));
    }, [selectedDayAvailability, isVoucherBooking]);

    const availableSlotsOverlayRows = useMemo(() => {
        const slots = selectedDayAvailability?.timeslots ?? [];

        // For vouchers we show no pricing, so make sure timeslot text is clean
        const normalizedSlots = isVoucherBooking
            ? slots.map((t) => ({ ...t, timeslot: hourLabel(t.hour) }))
            : slots;

        return groupTimeslotsForOverlay(normalizedSlots, { isVoucherBooking });
    }, [selectedDayAvailability, isVoucherBooking]);

    useEffect(() => {
        // Only applies to exact time input mode
        if (showTimeInput !== true) return;

        // Override: any time allowed
        if (overrideAvailableDates === true) return;

        // POA: any time allowed
        if (isPoaForPeriod === true) return;

        // No available slots for the selected day -> can’t validate/clamp
        if (!availableSlotHours || availableSlotHours.length === 0) return;

        // Ensure we have a HH:mm string
        const parsed =
            typeof timeValue === "string"
                ? parseMinuteFromTimeString(timeValue)
                : null;

        if (!parsed) return;

        const { hh, mm, minutes } = parsed;

        // If the typed hour exists in availability, it's valid
        if (availableSlotHours.includes(hh)) return;

        // Clamp hour to nearest available hour, keep minutes as typed
        const nearestHour = clampToNearestAvailableHour(
            minutes,
            availableSlotHours,
        );
        if (nearestHour === null) return;

        const next = `${pad2(nearestHour)}:${pad2(mm)}`;

        if (next !== timeValue) {
            onChangeTime(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showTimeInput,
        overrideAvailableDates,
        isPoaForPeriod,
        availableSlotHours,
        timeValue,
    ]);

    useEffect(() => {
        return () => {
            if (monthChangeTimerRef.current)
                clearTimeout(monthChangeTimerRef.current);
        };
    }, []);

    useEffect(() => {
        initialAutoselectDoneRef.current = false;
    }, [course, member, voucher, isCreditBooking]);

    useEffect(() => {
        const getDates = async () => {
            if (!member || !course) return;
            if (overrideAvailableDates === true) return;

            const seq = ++requestSeqRef.current;
            const requestedMonth = month;
            const requestedYear = year;

            let availability = [];

            try {
                if (isCreditBooking === true) {
                    availability = await GolfCourse.creditAvailability(
                        course,
                        requestedMonth + 1,
                        requestedYear,
                        member,
                        voucher,
                        bookingId,
                    );
                } else {
                    availability = await GolfCourse.availability(
                        course,
                        requestedMonth + 1,
                        requestedYear,
                        member,
                        voucher,
                    );
                }
            } catch (e) {
                return;
            }

            // ✅ If another request started after this one, ignore this response
            if (seq !== requestSeqRef.current) return;

            if (!Array.isArray(availability)) availability = [];

            // ✅ Ensure we’re still on the same month/year that this request was for
            if (requestedMonth !== month || requestedYear !== year) return;

            // Keep the selected date availability in sync
            let selectedDate = null;

            for (const available of availability) {
                let availableAsDate = new Date();
                availableAsDate = setYear(availableAsDate, requestedYear);
                availableAsDate = setMonth(availableAsDate, requestedMonth);
                availableAsDate = setDate(availableAsDate, available.day);

                if (isSameDay(availableAsDate, dateValue)) {
                    selectedDate = availableAsDate;
                    setSelectedDayAvailability(available);
                    break;
                }
            }

            // Only do "defaultFirstAvailable" once (initial load), and only for the latest response
            if (
                selectedDate === null &&
                availability.length > 0 &&
                defaultFirstAvailable === true &&
                initialAutoselectDoneRef.current === false
            ) {
                initialAutoselectDoneRef.current = true;

                const first = availability[0];
                let nextDate = new Date();
                nextDate = setYear(nextDate, requestedYear);
                nextDate = setMonth(nextDate, requestedMonth);
                nextDate = setDate(nextDate, first.day);

                setSelectedDayAvailability(first);

                // ❗ Do NOT setSelectedMonth/Year here (this is what causes month flicker)
                // Only notify parent of a date selection on initial load
                onChangeDate(nextDate);
            }

            if (selectedDate === null) {
                setSelectedDayAvailability(null);
            }

            setAvailableDates(availability);

            if (typeof onLoadedAvailability === "function") {
                onLoadedAvailability(availability);
            }
        };

        getDates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        member,
        course,
        voucher,
        isCreditBooking,
        month,
        year,
        overrideAvailableDates,
        bookingId,
    ]);

    useEffect(() => {
        // Only reset time when using the hour dropdown, not the exact time input
        if (showTimeslotSelection !== true || showTimeInput === true) return;

        onChangeTime(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedDayAvailability,
        overrideAvailableDates,
        month,
        year,
        showTimeInput,
    ]);

    const getDatePickerProps = () => {
        const properties = {
            onMonthChange: (m) => {
                setAvailableDates([]);

                if (monthChangeTimerRef.current) {
                    clearTimeout(monthChangeTimerRef.current);
                }

                monthChangeTimerRef.current = setTimeout(() => {
                    setSelectedMonth(m.getMonth());
                    setSelectedYear(m.getFullYear());
                }, 150);
            },
            firstDayOfWeek: 1,
        };

        if (overrideAvailableDates === false) {
            properties.modifiers = {
                selected: (day) => isSameDay(day, dateValue),
            };
            properties.modifiersStyles = {
                selected: { color: "white", backgroundColor: "#5d637d" },
            };

            // Month-level POA => allow all days (same as override day-blocking behaviour)
            if (isPoaForPeriod === true) {
                properties.disabledDays = { before: new Date() };
            } else {
                // Disable days NOT present in availableDates
                properties.disabledDays = (dateOption) => {
                    return (
                        availableDates.some(({ day }) => {
                            let available = new Date();
                            available = setYear(available, year);
                            available = setMonth(available, month);
                            available = setDate(available, day);
                            return isSameDay(dateOption, available);
                        }) === false
                    );
                };
            }
        } else {
            properties.disabledDays = { before: new Date() };
        }

        return properties;
    };

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
            }}
        >
            <DatePicker
                value={dateValue}
                onDayChange={(value) => {
                    const availabilityForDate =
                        availableDates.find(
                            ({ day }) => day === value.getDate(),
                        ) ?? null;

                    setSelectedDayAvailability(
                        overrideAvailableDates ? null : availabilityForDate,
                    );

                    onChangeDate(value);

                    if (
                        showTimeslotSelection === true &&
                        showTimeInput !== true
                    ) {
                        onChangeTime(null);
                    }
                }}
                dayPickerProps={getDatePickerProps()}
            />

            {showTimeslotSelection && (
                <Select
                    // value is numeric hour in the new world
                    // but keep backward compatible if caller still supplies a string
                    value={
                        typeof timeValue === "number"
                            ? timeValue
                            : timeValueHour ?? null
                    }
                    className='ml-3'
                    onChange={(_, { value }) => {
                        // value is numeric hour
                        onChangeTime(value);
                    }}
                    placeholder='Please select a time slot'
                    options={currentOptions}
                />
            )}

            {showTimeInput && (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Input
                        type='time'
                        value={timeValue}
                        placeholder='Please choose a time'
                        onChange={({ target }) => onChangeTime(target.value)}
                    />

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                    >
                        {/* Show button only when not override */}
                        <Button
                            type='button'
                            size='mini'
                            basic
                            icon
                            disabled={
                                overrideAvailableDates === true ||
                                (isPoaForPeriod === false &&
                                    availableSlotsForHelper.length === 0)
                            }
                            onClick={() => setShowAvailableTimesModal(true)}
                            title='View available times'
                        >
                            <Icon name='clock outline' />
                        </Button>

                        {overrideAvailableDates === true ? (
                            <span style={{ fontSize: 12, opacity: 0.75 }}>
                                Override enabled — all times can be chosen.
                            </span>
                        ) : isPoaForPeriod === true ? (
                            <span style={{ fontSize: 12, opacity: 0.75 }}>
                                POA, all times can be chosen.
                            </span>
                        ) : availableSlotsForHelper.length === 0 ? (
                            <span style={{ fontSize: 12, opacity: 0.75 }}>
                                Choose another day.
                            </span>
                        ) : (
                            <span style={{ fontSize: 12, opacity: 0.75 }}>
                                {availableSlotsForHelper.length} available times
                            </span>
                        )}

                        <Modal
                            open={showAvailableTimesModal}
                            onClose={() => setShowAvailableTimesModal(false)}
                            size='tiny'
                        >
                            <Modal.Header>Available times</Modal.Header>
                            <Modal.Content scrolling>
                                {overrideAvailableDates === true ? (
                                    <p>
                                        Override enabled — all times can be
                                        chosen.
                                    </p>
                                ) : isPoaForPeriod === true ? (
                                    <p>POA, all times can be chosen.</p>
                                ) : availableSlotsForHelper.length === 0 ? (
                                    <p>Choose another day.</p>
                                ) : (
                                    <div style={{ display: "grid", gap: 6 }}>
                                        {availableSlotsOverlayRows.map(
                                            (row) => (
                                                <div key={row.key}>
                                                    {row.text}
                                                </div>
                                            ),
                                        )}
                                    </div>
                                )}
                            </Modal.Content>
                            <Modal.Actions>
                                <Button
                                    onClick={() =>
                                        setShowAvailableTimesModal(false)
                                    }
                                >
                                    Close
                                </Button>
                            </Modal.Actions>
                        </Modal>
                    </div>
                </div>
            )}

            <Checkbox
                toggle
                label='Override available dates'
                checked={overrideAvailableDates}
                className='ml-3'
                onChange={() => {
                    const next = overrideAvailableDates === false;
                    setOverrideAvailableDates(next);

                    if (typeof onOverrideChange === "function") {
                        onOverrideChange(next);
                    }

                    // If switching into override, clear day availability so the dropdown uses all 24 hours
                    if (next === true) {
                        setSelectedDayAvailability(null);
                    } else {
                        // switching out of override: selectedDayAvailability will be reset by fetch effect
                    }

                    if (
                        showTimeslotSelection === true &&
                        showTimeInput !== true
                    ) {
                        onChangeTime(null);
                    }
                }}
            />
        </div>
    );
};

export default CourseAvailabilityCalendar;
